<template>
    <module-item-with-slots
        v-bind="$attrs"
        :title="props.manualTitle || props.isPopout ? undefined : props.title"
        :hide-date="props.hideDate"
        :hide-icons="props.hideIcons"
        :blocked-actions="footerBlockedActions"
        :no-data="noData"
        :no-access="noAccess"
        :has-left-options="showLeftFooterSection"
        :is-modal="props.isModal"
        :hide-card-styling="props.hideCardStyling"
        :custom-popout="props.customPopout"
        :display-options-top="props.displayOptionsTop"
        :collision-padding-top="props.collisionPaddingTop"
        @clone:module="cloneModule"
        @delete:module="deleteModule"
        @edit:module="editChartModule"
        @fullscreen:module="showFullscreen"
        @pop-out:module="emit('pop-out:module')"
    >
        <template #left="{ updatePopoverVisibility }" v-if="showLeftFooterSection">
            <module-chart-options
                :chart-config="props.chartConfig"
                :current-view="currentView"
                :is-table-download-loading="isTableDownloadLoading"
                :can-metric="canMetric"
                :can-table="canTable"
                :blocked-actions="chartOptionsBlockedActions"
                :update-popover-visibility="updatePopoverVisibility"
                :show-popout-icon="!props.isPopout"
                :no-data="noData"
                :disable-currency-conversion="props.disableCurrencyConversion || noAccess"
                :disable-axis-options="props.disableAxisOptions || noAccess"
                :currency-override="currencyOverride"
                :vertical-line="chartVerticalLineValue"
                :show-time="chartShowTimeValue"
                :show-legend="chartShowLegendValue"
                :y-axis-array="(yAxisArrayValue as Array<IAxisLimits>)"
                :multiple-axis-options="props.multipleAxisOptions"
                @download-chart="downloadChart"
                @set:current-view="setCurrentView"
                @toggle-table-download="toggleTableDownload"
                @toggle-table-edit="toggleTableEdit"
                @update:y-axis-limits="updateYAxisLimits"
                @update:x-axis-current-line="updateXAxisCurrentLine"
                @toggle:x-axis="toggleXAxis"
                @update:show-legend="updateShowLegend"
                @update:currency="updateCurrency"
                @update:multiple-axis-options="updateMultipleAxisOptions"
            />
        </template>
        <template #right="{ isModal: datePickerIsModal }" v-if="slots.extra || !props.hideDate">
            <slot name="extra" />
            <module-header-date
                v-if="!props.hideDate"
                :current-date="formattedDateString"
                :timezone-label="timezoneLabel"
                :is-modal="datePickerIsModal"
                :disabled="noAccess"
            >
                <template #default="{ closeDatePicker }">
                    <chart-date-picker
                        :selected-date="localDatePickerSettings"
                        :default-selected-date="defaultDatePickerSettings"
                        :granularity-mode="chartDatePickerGranularityMode"
                        :date-locked="dateSelectionIsLocked"
                        :timezone-locked="timezoneSelectionIsLocked"
                        :period-frequency-locked="periodFrequencySelectionIsLocked"
                        :granularity-locked="granularitySelectionIsLocked"
                        :timezone-id="props.timezone ?? undefined"
                        :periods-per-day="props.periodsPerDay ?? undefined"
                        :show-timezone="props.showTimezone"
                        :only-allow-single-day="props.onlyAllowSingleDay"
                        :minimum-view="props.minimumView"
                        :min-date="props.minDate"
                        :max-date="props.maxDate"
                        @update:selected-date="updateDatePickerSettings"
                        @update:timezone-settings="updateDatePickerSettings"
                        @close:picker="closeDatePicker"
                    />
                </template>
            </module-header-date>
        </template>
        <template #content>
            <div class="module-chart-content">
                <module-chart
                    ref='chartRef'
                    :activeViewMode="currentView"
                    :currentViewIndex="props.currentViewIndex"
                    :currentModuleIndex="defaultModuleIndex"
                    :chartConfig="props.chartConfig"
                    :chartData="props.chartData"
                    :title="props.title"
                    :chartLegendHeightOverride="props.chartLegendHeightOverride"
                    :fixed-legend="props.fixedLegend"
                    :moveLegendRight="props.moveLegendRight"
                    :clearSeriesOnRemove="props.clearSeriesOnRemove"
                    :noPushes="props.noPushes"
                    :isDelta="props.isDelta"
                    :nonDeltaSeries="props.nonDeltaSeries"
                    :hideNavigator="props.hideNavigator"
                    :disableDrilldownProp="props.disableDrilldownProp"
                    :currencyOverride="currencyOverride"
                    :customDateOverride="props.dateOverride"
                    :is-fixed-module="props.isFixedModule"
                    :manual-title="props.manualTitle"
                    :isForIndex="props.isForIndex"
                    :allow-module-chart-table-excel-download="props.allowModuleChartTableExcelDownload"
                    :force-light-theme="props.forceLightTheme"
                    :y-axis-normalization-group-key="props.yAxisNormalizationGroupKey"
                    allow-select-timezone
                    @update:extra-chart-info="updateExtraChartInfo"
                    @open:plant-drilldown-modal="openDrilldownModal"
                    @open:plant-modal="openPlantModal"
                    @update:isAggregate="updateIsAggregate"
                    @update:no-data="updateNoData"
                    @update:no-access="updateNoAccess"
                />
                <info-icon-with-tooltip
                    v-if="props.warningTooltip"
                    class="module-warning"
                    :tooltip-text="props.warningTooltip"
                    variant="warning"
                    direction="top"
                />
            </div>
        </template>
    </module-item-with-slots>
    <plant-switch
        v-if="plantModal.plantId"
        v-model:visible="plantModal.open"
        :plant-id="plantModal.plantId"
        :from-date="plantModal.fromDate"
        :to-date="plantModal.toDate"
    />
    <central-modal
        v-model:visible="drilldownModal.open"
        :header="drilldownModal.title"
        size="medium"
    >
        <drilldown-page
            v-if="drilldownModal.id"
            :id="drilldownModal.id"
            :fromDate="drilldownModal.fromDate"
            :toDate="drilldownModal.toDate"
        />
    </central-modal>
</template>

<script setup lang="ts">
import {
    computed, nextTick, ref, useSlots, watch,
} from 'vue';
import ModuleChartOptions from '@/components/powerMarkets/chart/ModuleChartOptions.vue';
import ChartDatePicker from '@/components/powerMarkets/chart/ChartDatePicker.vue';
import CentralModal from '@/components/common/CentralModal.vue';
import {
    IAxisLimits, IModuleChartConfig, ViewMode,
} from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IChartMultipleAxisOptions } from '@/interfaces/powerMarkets/charts/IChartMultipleAxisOptions.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { ISeriesList } from '@/interfaces/powerMarkets/series/ISeriesList.ts';
import {
    dateFormatToAnyFormat, generateDatePickerSettings, getDateObjectFromDatePicker, getLocalDatePickerSettingsString,
} from '@/utils/dateUtils.ts';
import { IAdvancedDatePickerModel, IAdvancedDatePickerUpdatePayload } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import ModuleItemWithSlots from '@/components/powerMarkets/dashboard/ModuleItemWithSlots.vue';
import ModuleHeaderDate from '@/components/powerMarkets/chart/ModuleHeaderDate.vue';
import { IDateRangeByNumberWithType } from '@/interfaces/general/IDateRangeByString.ts';
import { IExtraChartInfo } from '@/interfaces/powerMarkets/dashboard/IDashboardPresetItemInfo.ts';
import ModuleChart from '@/components/powerMarkets/chart/ModuleChart.vue';
import { useUserSettingsActions } from '@/actions/powerMarkets/userSettingsActions.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { TDatePickerMinimumView } from '@/types/powerMarkets/TDatePickerMinimumView.ts';
import { TDatePickerGranularityMode } from '@/types/powerMarkets/TDatePickerGranularityMode.ts';
import {
    IModuleBlockedActions, NO_ACCESS_BLOCKED_ACTIONS, NON_USER_DASHBOARD_BLOCKED_ACTIONS,
} from '@/interfaces/actions/enact/IModuleActions.ts';
import { buildChartModulePayloadFromChartConfig } from '@/components/powerMarkets/modals/dashboard/AddModuleModal/helpers.ts';
import { IReportCsvSection } from '@/interfaces/reports/IReportExport.ts';
import { getTimezoneOffsetLabel } from '@/utils/timezoneUtils.ts';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import DrilldownPage from '../common/DrilldownPage.vue';
import PlantSwitch from '../plant/PlantSwitch.vue';

const slots = useSlots();

const props = defineProps<{
    currentModuleIndex?: number; // Default is -1
    currentViewIndex?: number; // Default is -1
    chartConfig: IModuleChartConfig;
    chartData?: ISeriesList;
    title?: string;
    isModal?: boolean;
    hideDate?: boolean;
    hideIcons?: boolean;
    moveLegendRight?: boolean;
    blockedActions?: IModuleBlockedActions;
    chartWidth?: number;
    isPopout?: boolean;
    clearSeriesOnRemove?: boolean;
    dateOverride?: IDashboardOverrides;
    noPushes?: boolean;
    onlyAllowSingleDay?: boolean;
    isDelta?: boolean;
    nonDeltaSeries?: ISeriesList | null;
    hideNavigator?: boolean;
    disableDrilldownProp?: boolean;
    disableAxisOptions?: boolean;
    disableCurrencyConversion?: boolean;
    moduleDate?: string | IDateObject | IDateRangeByNumberWithType;
    chartLegendHeightOverride?: number;
    fixedLegend?: boolean;
    manualTitle?: boolean;
    isFixedModule?: boolean;
    isForIndex?: boolean;
    showTimezone?: boolean;
    timezone?: string | null;
    periodsPerDay?: number | null;
    timezoneSelectionLocked?: boolean;
    periodFrequencySelectionLocked?: boolean;
    granularitySelectionLocked?: boolean;
    dateSelectionLocked?: boolean;
    minimumView?: TDatePickerMinimumView;
    hideCardStyling?: boolean;
    customPopout?: boolean;
    shouldEmitClone?: boolean;
    multipleAxisOptions?: IChartMultipleAxisOptions | null;
    forceLightTheme?: boolean;
    minDate?: Date;
    maxDate?: Date;
    displayOptionsTop?: boolean;
    allowModuleChartTableExcelDownload?: boolean;
    warningTooltip?: string;
    collisionPaddingTop?: number;
    yAxisNormalizationGroupKey?: string | number;
}>();

const defaultModuleIndex = props.currentModuleIndex ?? -1;

const emit = defineEmits<{
    'delete:module': [];
    'update:chart-date': [IDateObject];
    'update:extra-chart-info': [IExtraChartInfo];
    'update:multiple-axis-options': [IChartMultipleAxisOptions];
    'edit:chart-module': [];
    'clone:module': [Array<string>];
    'pop-out:module': [];
}>();

const userSettingsStore = useUserSettingsStore();
const userSettingsActions = useUserSettingsActions();

const currentView = ref<ViewMode>(ViewMode.Chart);
const isAggregate = ref(false);
const noData = ref<boolean>(false);
const noAccess = ref<boolean>(false);
const isTableDownloadLoading = ref<boolean>(false);

const chartRef = ref<InstanceType<typeof ModuleChart> | null>(null);

const currencyOverride = ref<string>(
    props.chartConfig.info.extraChartInfo?.aggregateOptionsProp?.conversionOptions?.newCurrency
    ?? props.chartConfig.info.extraChartInfo?.aggregateOptionsProp?.conversionOptions?.currencyOverride
    ?? 'default',
);

const plantModal = ref({
    plantId: '',
    fromDate: new Date(),
    toDate: new Date(),
    open: false,
});

const drilldownModal = ref({
    id: '',
    title: '',
    fromDate: new Date(),
    toDate: new Date(),
    open: false,
});

const chartShowTimeValue = computed<boolean>(() => chartRef.value?.showTime ?? true);
const chartShowLegendValue = computed<boolean>(() => (
    chartRef.value?.showLegend
    ?? props.chartConfig.info.extraChartInfo?.showLegend
    ?? true
));
const chartVerticalLineValue = computed<'1min' | '5min' | 'period'>(() => (
    (chartRef.value?.currentTimeLine as '1min' | '5min' | 'period' | undefined) ?? 'period'
));
const yAxisArrayValue = computed(() => {
    const yAxis = chartRef.value?.chartOptions?.yAxis;
    if (Array.isArray(yAxis)) {
        return yAxis;
    } if (yAxis) {
        return [yAxis];
    }
    return [];
});

const timezoneLabel = computed<string | null>(() => {
    const timezoneToUse = props.timezone ?? userSettingsStore.sitewideTimezone;
    return getTimezoneOffsetLabel(timezoneToUse, userSettingsStore.sitewideTimezone);
});

const localDatePickerSettings = ref<IAdvancedDatePickerModel | null>(null);
const defaultDatePickerSettings = computed<IAdvancedDatePickerModel>(() => generateDatePickerSettings(
    props.chartConfig.info.date,
    props.moduleDate,
    props.chartConfig.info.extraChartInfo?.aggregateOptionsProp,
));
const formattedDateString = computed<string>(() => getLocalDatePickerSettingsString(
    localDatePickerSettings.value,
    props.onlyAllowSingleDay,
    props.minimumView ?? 'day',
));

const dateSelectionIsLocked = computed<boolean>(() => {
    const chartOverride = props.dateOverride?.chart;
    return !!props.dateSelectionLocked || !!chartOverride?.from || !!chartOverride?.to;
});

const granularitySelectionIsLocked = computed<boolean>(() => {
    const chartOverride = props.dateOverride?.chart;
    return !!props.granularitySelectionLocked
        || !!chartOverride?.from
        || !!chartOverride?.to
        || !!chartOverride?.aggregateOptions?.granularity;
});

const timezoneSelectionIsLocked = computed<boolean>(() => (
    !!props.timezoneSelectionLocked || !!props.dateOverride?.chart?.timezone
));

const periodFrequencySelectionIsLocked = computed<boolean>(() => (
    !!props.periodFrequencySelectionLocked || !!props.dateOverride?.chart?.periodsPerDay
));

const hasTime = computed<boolean>(() => chartRef.value?.hasTime ?? false);
const chartDatePickerGranularityMode = computed<TDatePickerGranularityMode>(() => {
    if (props.isForIndex) return 'index';
    if (!props.onlyAllowSingleDay && !props.chartData && hasTime.value) return 'standard';
    return 'none';
});
const isSmallWidth = computed<boolean>(() => {
    if (props.chartWidth) {
        return props.chartWidth < 5 && !props.isPopout;
    }
    return false;
});
const canTable = computed<boolean>(() => !isSmallWidth.value);
const canMetric = computed<boolean>(() => !props.chartData);
const showHelpAction = computed<boolean>(() => !chartOptionsBlockedActions.value?.explain && !!props.chartConfig.data.uniqueSeriesNoOption.length);
const showDownloadAction = computed<boolean>(() => !chartOptionsBlockedActions.value?.download && !noData.value);
const showAxisOptionsAction = computed<boolean>(() => !props.disableAxisOptions && !noAccess.value);
const showCurrencyAction = computed<boolean>(() => !props.disableCurrencyConversion && !noAccess.value && props.chartConfig.data.config.some(configValue => !!configValue.currency));
const showLeftFooterSection = computed<boolean>(() => showHelpAction.value || showDownloadAction.value || (
    !noData.value && (canTable.value || canMetric.value || currentView.value === ViewMode.Table)
) || showAxisOptionsAction.value || showCurrencyAction.value);

const baseBlockedActions = computed<IModuleBlockedActions>(() => {
    const blocked: IModuleBlockedActions = {
        ...(props.isFixedModule ? NON_USER_DASHBOARD_BLOCKED_ACTIONS : props.blockedActions ?? {}),
    };
    if (currentView.value === ViewMode.Table) blocked.fullscreen = true;
    if (props.chartData) blocked.clone = true;
    return blocked;
});

const footerBlockedActions = computed<IModuleBlockedActions>(() => baseBlockedActions.value);

const chartOptionsBlockedActions = computed<IModuleBlockedActions>(() => {
    if (noAccess.value) {
        return { ...props.blockedActions, ...NO_ACCESS_BLOCKED_ACTIONS };
    }
    return props.blockedActions ?? {};
});

function updateYAxisLimits (limits: Array<IAxisLimits>): void {
    chartRef.value?.changeYAxisLimits(limits);
}

function toggleXAxis (): void {
    chartRef.value?.toggleTime();
}

function updateXAxisCurrentLine (option: string): void {
    chartRef.value?.setCurrentTimeLine(option);
}

function updateShowLegend (showLegend: boolean): void {
    chartRef.value?.setLegendVisibility(showLegend);
}

function editChartModule (): void {
    emit('edit:chart-module');
}

function updateCurrency (currency: string): void {
    currencyOverride.value = currency;
    nextTick(() => {
        chartRef.value?.updateCurrency();
    });
}

function updateIsAggregate (value: boolean): void {
    isAggregate.value = value;
}

function updateNoData (updatedNoData: boolean): void {
    noData.value = updatedNoData;
}

function updateNoAccess (updatedNoAccess: boolean): void {
    noAccess.value = updatedNoAccess;
}

async function runTableDownload (dateOverride?: IAdvancedDatePickerModel): Promise<void> {
    if (isTableDownloadLoading.value) return;

    isTableDownloadLoading.value = true;
    try {
        await chartRef.value?.downloadTable(dateOverride);
    } finally {
        isTableDownloadLoading.value = false;
    }
}

async function toggleTableDownload (): Promise<void> {
    await runTableDownload();
}

async function getReportCsvSections (): Promise<Array<IReportCsvSection>> {
    const rows = await chartRef.value?.getReportCsvRows?.();
    if (!rows?.length) return [];
    return [{
        title: props.title ?? props.chartConfig.info.name ?? 'Chart',
        rows,
    }];
}
function toggleTableEdit (event: Event): void {
    chartRef.value?.editTable(event);
}

function showFullscreen (): void {
    chartRef.value?.showFullscreen();
}

function downloadChart (): void {
    chartRef.value?.downloadChart();
}

function setCurrentView (newView: ViewMode): void {
    currentView.value = newView;
}

function openPlantModal (plantId: string): void {
    if (!props.chartConfig.info.date) return;

    const matchDate = dateFormatToAnyFormat<{
        from: Date;
        to: Date;
    }>(props.chartConfig.info.date, 'Date');

    plantModal.value = {
        plantId,
        fromDate: matchDate.from,
        toDate: matchDate.to,
        open: true,
    };
}

function openDrilldownModal (id: string, title: string): void {
    if (!props.chartConfig.info.date) return;

    const matchDate = dateFormatToAnyFormat<{
        from: Date;
        to: Date;
    }>(props.chartConfig.info.date, 'Date');

    drilldownModal.value = {
        id,
        title,
        fromDate: matchDate.from,
        toDate: matchDate.to,
        open: true,
    };
}

function updateLocalValues (): void {
    const chartOverride = props.dateOverride?.chart;
    const overrideDefinesRange = Boolean(chartOverride?.from && chartOverride?.to);

    if (chartOverride && overrideDefinesRange) {
        localDatePickerSettings.value = generateDatePickerSettings(
            undefined,
            chartOverride,
            chartOverride.aggregateOptions,
        );
    } else {
        localDatePickerSettings.value = generateDatePickerSettings(
            props.chartConfig.info.date,
            props.moduleDate,
            chartOverride?.aggregateOptions
                ?? props.chartConfig.info.extraChartInfo?.aggregateOptionsProp,
        );

        if (chartOverride) {
            if (chartOverride.timezone != null) {
                localDatePickerSettings.value.timezone = chartOverride.timezone;
            }
            if (chartOverride.periodsPerDay != null) {
                localDatePickerSettings.value.periodsPerDay = chartOverride.periodsPerDay;
            }
        }
    }

    if (props.chartConfig.info.extraChartInfo?.currentDisplayOption) {
        currentView.value = props.chartConfig.info.extraChartInfo.currentDisplayOption;
    }
}

function getCurrentDatePickerSettings (): IAdvancedDatePickerModel {
    return localDatePickerSettings.value ?? generateDatePickerSettings(
        props.chartConfig.info.date,
        props.moduleDate,
        props.chartConfig.info.extraChartInfo?.aggregateOptionsProp,
    );
}

async function updateDatePickerSettings (settings: IAdvancedDatePickerUpdatePayload | null): Promise<void> {
    if (!settings) return;

    localDatePickerSettings.value = {
        ...getCurrentDatePickerSettings(),
        ...settings,
    };

    emit('update:chart-date', getDateObjectFromDatePicker(localDatePickerSettings.value));
    const aggregateUpdate = Object.prototype.hasOwnProperty.call(settings, 'aggregate')
        ? (settings.aggregate ?? null)
        : undefined;
    await chartRef.value?.dateChange(aggregateUpdate);
}

function updateExtraChartInfo (extraChartInfo: IExtraChartInfo): void {
    emit('update:extra-chart-info', extraChartInfo);
}

function updateMultipleAxisOptions (multipleAxisOptions: IChartMultipleAxisOptions): void {
    emit('update:multiple-axis-options', multipleAxisOptions);
    chartRef.value?.updateMultipleAxisOptions();
}

function deleteModule (): void {
    emit('delete:module');
}

function getCloneModuleDate (): IDateObject | undefined {
    if (localDatePickerSettings.value) {
        return getDateObjectFromDatePicker(localDatePickerSettings.value);
    }

    if (props.chartConfig.info.date) {
        return dateFormatToAnyFormat<IDateObject>(props.chartConfig.info.date, 'IDateObject');
    }

    return undefined;
}

async function cloneModule (dashboardIds: Array<string>): Promise<void> {
    if (footerBlockedActions.value.clone) return;

    if (props.shouldEmitClone) {
        emit('clone:module', dashboardIds);
        return;
    }

    const module = buildChartModulePayloadFromChartConfig(props.chartConfig, 'dashboard');
    module.name = props.title ?? props.chartConfig.info.name ?? module.name;

    const selectedDate = getCloneModuleDate();

    if (selectedDate) {
        module.date = selectedDate;
    }

    module.extraChartInfo = {
        ...(props.chartConfig.info.extraChartInfo ?? {}),
        currentDisplayOption: currentView.value,
        currentTimeLine: chartVerticalLineValue.value,
        showTime: chartShowTimeValue.value,
        showLegend: chartShowLegendValue.value,
    };

    userSettingsStore.cloneModuleToDashboards(module, dashboardIds);
    await userSettingsActions.updateDashboards(dashboardIds, undefined, true);
}

function initialise (): void {
    updateLocalValues();
}

initialise();

watch(() => props.dateOverride?.chart, () => {
    updateLocalValues();
});

defineExpose({
    getReportCsvSections,
});
</script>

<style scoped lang="scss">
.module-chart-content {
    position: relative;
    width: 100%;
    height: 100%;
}

.module-warning {
    position: absolute;
    top: 3px;
    left: 10px;
    z-index: 1;
}
</style>
